/**
 * Feature flag catalog + tier-based resolution.
 *
 * Source of truth for what each subscription tier (and paid plan level)
 * unlocks in the UI. Lambdas independently enforce server-side; this
 * module is the UI gate only.
 *
 * Decision rules:
 *   - Any platform role (super_admin / auditor / support_engineer) → every flag on
 *   - Tenant roles on tier=Demo / POC / Trial                       → every flag on (eval experience)
 *   - Tenant roles on tier=Paid                                     → driven by plan_level
 *   - Missing tier (user.tier == null/undefined)                    → safest defaults (Starter)
 */

import type { SubscriptionTier, PlanLevel, UserRole } from '@/types';
import { isPlatformRole } from '@/lib/permissions';

// ─── Feature catalog ────────────────────────────────────────────────────────

/**
 * Add new feature flag keys here. Each must be wired into FEATURE_MATRIX below
 * so it can be evaluated for any tier + plan level.
 */
export type FeatureFlag =
  | 'aiAnalysis'
  | 'tokenObservability'
  | 'multiProviderSync'
  | 'sso'
  | 'auditLogExport'
  | 'customRetention'
  | 'budgetEnforcement'
  | 'apiAccess'
  // EU AI Act module — Enterprise-tier add-on per GTM pricing.
  | 'aiActModule';

// ─── Tier matrix ────────────────────────────────────────────────────────────

type PaidMatrix = Record<PlanLevel, readonly FeatureFlag[]>;

interface FeatureMatrix {
  demo: readonly FeatureFlag[];
  poc: readonly FeatureFlag[];
  trial: readonly FeatureFlag[];
  paid: PaidMatrix;
}

/**
 * Source of truth. Demo / POC / Trial all unlock everything so prospects can
 * explore the full product. Paid customers see exactly what they bought.
 */
const ALL_FLAGS: readonly FeatureFlag[] = [
  'aiAnalysis',
  'tokenObservability',
  'multiProviderSync',
  'sso',
  'auditLogExport',
  'customRetention',
  'budgetEnforcement',
  'apiAccess',
  'aiActModule',
];

const FEATURE_MATRIX: FeatureMatrix = {
  demo: ALL_FLAGS,
  poc: ALL_FLAGS,
  trial: ALL_FLAGS,
  paid: {
    starter: ['aiAnalysis', 'tokenObservability'],
    professional: [
      'aiAnalysis',
      'tokenObservability',
      'multiProviderSync',
      'budgetEnforcement',
      'apiAccess',
    ],
    enterprise: ALL_FLAGS,
  },
};

// ─── Resolver ───────────────────────────────────────────────────────────────

interface ResolveInput {
  role: UserRole | null | undefined;
  tier: SubscriptionTier | null | undefined;
  planLevel: PlanLevel | null | undefined;
}

export function resolveFlag(flag: FeatureFlag, input: ResolveInput): boolean {
  // Platform roles see everything regardless of tier.
  if (isPlatformRole(input.role)) return true;

  // No tier known yet — conservative default (Starter scope).
  if (!input.tier) {
    return FEATURE_MATRIX.paid.starter.includes(flag);
  }

  if (input.tier === 'paid') {
    const level = input.planLevel ?? 'starter';
    return FEATURE_MATRIX.paid[level].includes(flag);
  }

  return FEATURE_MATRIX[input.tier].includes(flag);
}

export function resolveAllFlags(input: ResolveInput): Record<FeatureFlag, boolean> {
  return ALL_FLAGS.reduce(
    (acc, flag) => {
      acc[flag] = resolveFlag(flag, input);
      return acc;
    },
    {} as Record<FeatureFlag, boolean>
  );
}
